import { useEffect, useMemo, type ReactNode } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  CheckCircle2,
  CircleAlert,
  CreditCard,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageShell } from "@/components/layout/PageShell";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { storefrontSdk } from "@/lib/sdk";
import { browserUuid } from "@/lib/browser-ids";
import { useStorefront } from "@/lib/storefront";
import {
  clearPaymentReturnContext,
  readPaymentReturnContext,
  writePaymentReturnContext,
} from "@/lib/payment-return-context";
import { readPaymentLinkReturnContext } from "@/lib/payment-link-return-context";
import {
  removeUnfinishedCheckoutCart,
  unfinishedCheckoutCartKey,
  updateUnfinishedCheckoutAttempt,
} from "@/lib/unfinished-checkout-cart";

export const Route = createFileRoute("/payment-return")({
  component: PaymentReturn,
});

const TERMINAL_FAILURES = new Set(["failed", "expired", "cancelled"]);
const MAX_AUTOMATIC_POLL_MS = 2 * 60_000;

function PaymentReturn() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const storefront = useStorefront();
  const { hydrated, isAuthenticated, openLogin, user } = useAuth();
  const context = useMemo(
    () =>
      typeof window === "undefined"
        ? null
        : readPaymentReturnContext(window.sessionStorage),
    [],
  );
  const paymentLinkContext = useMemo(
    () =>
      typeof window === "undefined"
        ? null
        : readPaymentLinkReturnContext(window.sessionStorage),
    [],
  );
  const automaticPolling =
    context !== null &&
    Date.now() - new Date(context.startedAt).getTime() < MAX_AUTOMATIC_POLL_MS;
  const attempt = useQuery({
    queryKey: ["payment-attempt", context?.attemptId],
    queryFn: () => storefrontSdk.paymentAttempt(context!.attemptId),
    enabled:
      storefront.mode === "published" &&
      hydrated &&
      isAuthenticated &&
      Boolean(context?.attemptId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return automaticPolling &&
        status !== "succeeded" &&
        !TERMINAL_FAILURES.has(status ?? "")
        ? 2_000
        : false;
    },
  });
  const reconcile = useMutation({
    mutationFn: () => storefrontSdk.reconcilePaymentAttempt(context!.attemptId),
    onSettled: () => void attempt.refetch(),
  });
  const retry = useMutation({
    mutationFn: () =>
      storefrontSdk.retryPaymentAttempt(context!.attemptId, {
        idempotencyKey: browserUuid(),
      }),
    onSuccess: (next) => {
      if (user?.id && context) {
        updateUnfinishedCheckoutAttempt(
          window.localStorage,
          unfinishedCheckoutCartKey(storefront.tenant.id, user.id),
          context.attemptId,
          next.id,
        );
      }
      writePaymentReturnContext(window.sessionStorage, {
        attemptId: next.id,
        orderId: next.orderId,
        startedAt: new Date().toISOString(),
      });
      if (next.nextAction?.type === "redirect")
        window.location.assign(next.nextAction.url);
      else window.location.reload();
    },
  });

  useEffect(() => {
    if (!paymentLinkContext) return;
    navigate({
      to: "/pay/$token",
      params: { token: paymentLinkContext.token },
      replace: true,
    });
  }, [navigate, paymentLinkContext]);

  useEffect(() => {
    if (
      storefront.mode !== "published" ||
      !hydrated ||
      !isAuthenticated ||
      !context
    )
      return;
    reconcile.mutate();
    // The provider return is only a hint; the signed callback/status lookup remains authoritative.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, hydrated, isAuthenticated, storefront.mode]);

  useEffect(() => {
    if (attempt.data?.status !== "succeeded" || !context) return;
    if (user?.id) {
      removeUnfinishedCheckoutCart(
        window.localStorage,
        unfinishedCheckoutCartKey(storefront.tenant.id, user.id),
        context.attemptId,
      );
    }
    clearPaymentReturnContext(window.sessionStorage);
    navigate({
      to: "/order-success",
      search: { orderId: context.orderId },
      replace: true,
    });
  }, [attempt.data?.status, context, navigate, storefront.tenant.id, user?.id]);

  if (storefront.mode === "preview") {
    return (
      <PaymentMessage
        icon="error"
        title={t("paymentReturn.previewTitle")}
        body={t("paymentReturn.previewBody")}
      />
    );
  }
  if (paymentLinkContext) {
    return (
      <PaymentMessage
        icon="loading"
        title={t("paymentReturn.checkingTitle")}
        body={t("paymentReturn.checkingBody")}
      />
    );
  }
  if (!context) {
    return (
      <PaymentMessage
        icon="error"
        title={t("paymentReturn.missingTitle")}
        body={t("paymentReturn.missingBody")}
        action={
          <Button asChild>
            <Link to="/orders">{t("nav.orders")}</Link>
          </Button>
        }
      />
    );
  }
  if (!hydrated)
    return (
      <PaymentMessage
        icon="loading"
        title={t("paymentReturn.checkingTitle")}
        body={t("paymentReturn.checkingBody")}
      />
    );
  if (!isAuthenticated) {
    return (
      <PaymentMessage
        icon="error"
        title={t("paymentReturn.signInTitle")}
        body={t("paymentReturn.signInBody")}
        action={<Button onClick={() => openLogin()}>{t("nav.signin")}</Button>}
      />
    );
  }
  if (attempt.isError) {
    return (
      <PaymentMessage
        icon="error"
        title={t("paymentReturn.errorTitle")}
        body={t("paymentReturn.errorBody")}
        action={
          <Button onClick={() => void attempt.refetch()}>
            <RefreshCw className="size-4" />
            {t("common.retry")}
          </Button>
        }
      />
    );
  }
  const status = attempt.data?.status;
  if (status && TERMINAL_FAILURES.has(status)) {
    return (
      <PaymentMessage
        icon="error"
        title={t("paymentReturn.failedTitle")}
        body={t("paymentReturn.failedBody")}
        action={
          <div className="flex flex-wrap justify-center gap-3">
            <Button disabled={retry.isPending} onClick={() => retry.mutate()}>
              {retry.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <CreditCard className="size-4" />
              )}
              {t("paymentReturn.retryPayment")}
            </Button>
            <Button asChild variant="outline">
              <Link to="/orders/$orderId" params={{ orderId: context.orderId }}>
                {t("paymentReturn.viewOrder")}
              </Link>
            </Button>
            <Button asChild variant="ghost">
              <Link to="/cart">{t("checkout.returnToCart")}</Link>
            </Button>
          </div>
        }
      />
    );
  }
  if (status === "requires_customer_action" && attempt.data?.nextAction) {
    return (
      <PaymentMessage
        icon="loading"
        title={t("paymentReturn.actionTitle")}
        body={t("paymentReturn.actionBody")}
        action={
          <div className="flex flex-wrap justify-center gap-2">
            <Button
              onClick={() =>
                window.location.assign(attempt.data!.nextAction!.url)
              }
            >
              <CreditCard className="size-4" />
              {t("paymentReturn.continuePayment")}
            </Button>
            <Button asChild variant="outline">
              <Link to="/cart">{t("checkout.returnToCart")}</Link>
            </Button>
          </div>
        }
      />
    );
  }
  return (
    <PaymentMessage
      icon={status === "succeeded" ? "success" : "loading"}
      title={t(
        status === "unknown"
          ? "paymentReturn.delayedTitle"
          : "paymentReturn.checkingTitle",
      )}
      body={t(
        status === "unknown"
          ? "paymentReturn.delayedBody"
          : "paymentReturn.checkingBody",
      )}
      action={
        !automaticPolling ? (
          <div className="flex flex-wrap justify-center gap-3">
            <Button
              disabled={reconcile.isPending}
              onClick={() => reconcile.mutate()}
            >
              <RefreshCw
                className={
                  reconcile.isPending ? "size-4 animate-spin" : "size-4"
                }
              />
              {t("paymentReturn.checkAgain")}
            </Button>
            <Button asChild variant="outline">
              <Link to="/orders/$orderId" params={{ orderId: context.orderId }}>
                {t("paymentReturn.viewOrder")}
              </Link>
            </Button>
          </div>
        ) : undefined
      }
    />
  );
}

function PaymentMessage({
  icon,
  title,
  body,
  action,
}: {
  icon: "loading" | "success" | "error";
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <PageShell size="narrow">
      <section
        className="surface-card mx-auto grid max-w-lg place-items-center gap-4 p-7 text-center sm:p-10"
        aria-live="polite"
      >
        <span className="grid size-16 place-items-center rounded-full bg-accent text-primary">
          {icon === "loading" ? (
            <Loader2 className="size-8 animate-spin" aria-hidden="true" />
          ) : icon === "success" ? (
            <CheckCircle2 className="size-8 text-success" aria-hidden="true" />
          ) : (
            <CircleAlert
              className="size-8 text-destructive"
              aria-hidden="true"
            />
          )}
        </span>
        <div>
          <h1 className="text-xl font-black">{title}</h1>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">{body}</p>
        </div>
        {action}
      </section>
    </PageShell>
  );
}
